// The capture glyph rides the CaptureGlyph chooser (Clappy on actor_assist,
// the Knot everywhere else) — the same swap SideRail, Login, Onboarding and
// the drawer already make. This bar is the PHONE's, so a direct Knot import
// here drew Vowric's mark on the one surface a household actually uses.
import { CaptureGlyph as Knot } from './CaptureGlyph';
import {
  knotSlotIndex,
  navHref,
  isGoldTab,
  badgeLabel,
  type NavModelProps,
  type NavDestination,
  type TabBadge,
} from './navModel';
import type { TopTab } from '../routes/route';
import './BottomNav.css';

// R1a: kept as a named re-export so existing importers (App, tests) that
// referenced BottomNavProps keep compiling — it's now the shared nav model.
export type BottomNavProps = NavModelProps & {
  /** The rail's destinations this bar leaves out. When non-empty the bar
   * grows a "More" item that opens them; when empty (the rail, or a pack
   * declaring no phone set) nothing changes. Derived in useNavModel, never
   * a hand-kept second list. */
  overflow?: NavDestination[];
  onMorePress?: () => void;
  moreOpen?: boolean;
};

// The locked PHONE shell: destinations · [Knot, raised, centred] ·
// destinations. The Knot is present on every screen, breathing at idle, and
// is THE capture action from anywhere. Career is a distinct "mode" — when
// active it highlights GOLD rather than the ordinary active colour every
// other tab uses; nothing else in this bar ever takes that colour.
//
// Destinations, active state and the gold rule all come from ../navModel —
// the SAME model the SideRail renders on tablet/desktop. This bar only owns
// the horizontal, split-around-the-Knot layout. Core hardening 2026-07-29,
// Item 8: `destinations` is now a prop (lib/useNavDestinations.ts resolves
// it) rather than the fixed NAV_DESTINATIONS module constant this file used
// to import directly.
export function BottomNav({
  active,
  onNavigate,
  knotState,
  onKnotPress,
  destinations,
  knotAmp,
  onKnotPointerDown,
  onKnotPointerUp,
  onKnotPointerCancel,
  onKnotLostPointerCapture,
  badges,
  overflow,
  onMorePress,
  moreOpen,
}: BottomNavProps) {
  // MORE IS A DESTINATION FOR LAYOUT PURPOSES (Huan, 2026-08-25). It occupies
  // a slot in the bar exactly as a tab does, so the Knot's centring — which
  // is knotSlotIndex(length / 2) — keeps working without a special case. It
  // simply is not a tab: it opens a sheet instead of navigating, so it is
  // counted here and rendered separately below.
  const hasMore = (overflow?.length ?? 0) > 0 && onMorePress !== undefined;
  // WHAT IS NEW BEHIND MORE (2026-10-08, Huan: "Review into More"). A count
  // that belongs to a tab More holds would otherwise vanish from the bar the
  // day that tab moved behind the door — so More carries the SUM of its own
  // destinations' badges, off the same shared `badges` map (never a second
  // count). Zero renders nothing, the same rule every tab's badge keeps.
  const moreBadge = hasMore ? sumBadges((overflow ?? []).map((d) => badges?.[d.tab])) : undefined;
  const moreCount = badgeLabel(moreBadge);
  const barLength = destinations.length + (hasMore ? 1 : 0);
  const slot = Math.floor(barLength / 2);
  const left = destinations.slice(0, slot);
  const right = destinations.slice(slot);
  // The column template follows the ACTUAL destination counts. It used to be
  // the fixed `1fr 1fr 76px 1fr 1fr` of Actor's 4-destination shell, which
  // silently assumed that count: a pack declaring a different number (the
  // Personal pack declares NONE — the Knot is the whole bar) pushed the Knot
  // into whatever column it happened to land in, hard against the left edge.
  // For 4 destinations this still evaluates to the original template exactly.
  const style = {
    '--nav-left': left.length,
    '--nav-right': right.length + (hasMore ? 1 : 0),
  } as preact.JSX.CSSProperties;
  return (
    <nav class="bottom-nav" aria-label="Primary" style={style}>
      {left.map((item) => (
        <NavItem
          key={item.tab}
          item={item}
          active={active === item.tab}
          onNavigate={onNavigate}
          badge={badges?.[item.tab]}
        />
      ))}
      <div class="bottom-nav__knot-slot">
        <button
          type="button"
          class="bottom-nav__knot-btn"
          aria-label="Capture"
          onClick={onKnotPress}
          onPointerDown={onKnotPointerDown}
          onPointerUp={onKnotPointerUp}
          onPointerCancel={onKnotPointerCancel}
          onLostPointerCapture={onKnotLostPointerCapture}
        >
          <Knot state={knotState} amp={knotAmp} />
        </button>
      </div>
      {right.map((item) => (
        <NavItem
          key={item.tab}
          item={item}
          active={active === item.tab}
          onNavigate={onNavigate}
          badge={badges?.[item.tab]}
        />
      ))}
      {hasMore ? (
        <button
          type="button"
          class={`bottom-nav__item bottom-nav__more${moreOpen ? ' bottom-nav__item--active' : ''}`}
          aria-haspopup="dialog"
          aria-expanded={moreOpen === true}
          data-testid="nav-more"
          onClick={onMorePress}
        >
          <span class="bottom-nav__dot" />
          {moreCount !== null ? (
            <span class="bottom-nav__badge" data-testid="nav-badge-more">
              {moreCount}
            </span>
          ) : null}
          More
          {moreCount !== null ? <span class="bottom-nav__sr">{moreCount} new</span> : null}
        </button>
      ) : null}
    </nav>
  );
}

/** The badges of several destinations as one: counts add, and the total is
 * "capped" if any part was (a 99+ inside it makes the sum a floor too). */
function sumBadges(parts: Array<TabBadge | undefined>): TabBadge | undefined {
  let count = 0;
  let capped = false;
  for (const b of parts) {
    if (!b || b.count <= 0) continue;
    count += b.count;
    capped = capped || b.capped;
  }
  return count > 0 ? { count, capped } : undefined;
}

function NavItem({
  item,
  active,
  onNavigate,
  badge,
}: {
  item: NavDestination;
  active: boolean;
  onNavigate: (tab: TopTab) => void;
  badge?: TabBadge;
}) {
  const classes = ['bottom-nav__item'];
  if (active) classes.push(isGoldTab(item.tab) ? 'bottom-nav__item--gold' : 'bottom-nav__item--active');
  // CV6b §4 — the count rides the DOT, which is the tab's own mark. The
  // label stays the label: a tab that reads "Chat 3" is a tab whose name
  // changes, and the width of the bottom bar is already the tightest space
  // in the app.
  const count = badgeLabel(badge);

  return (
    <a
      class={classes.join(' ')}
      href={navHref(item.tab)}
      aria-current={active ? 'page' : undefined}
      onClick={(e) => {
        e.preventDefault();
        onNavigate(item.tab);
      }}
    >
      <span class="bottom-nav__dot" />
      {count !== null ? (
        <span class="bottom-nav__badge" data-testid={`nav-badge-${item.tab}`}>
          {count}
        </span>
      ) : null}
      {item.short}
      {count !== null ? <span class="bottom-nav__sr">{count} unread</span> : null}
    </a>
  );
}
